import React from 'react';
import DescriptionIcon from '@material-ui/icons/Description';
import type {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from 'ag-grid-community';

import { ActionIcon } from '@mummssoftware/common-ui';
import type { PatientConversationSummary } from '@mummssoftware/utils';

import {
  conversationTopic,
  formatDateTime,
  formatDuration,
  modelFamily,
  personLabel,
} from '../../utils/formatters';
import defaults from '../../translations/en.json';
import { getTokenColumnDefs, TokenCells } from './tokenColumnDefs';

type ColumnDefsArgs = {
  onOpenPast: (row: PatientConversationSummary) => void;
  smallWidget?: boolean;
  siteTimezone?: string | null;
  /** "See tokens" is on (Mumms admins): token split and cost columns. */
  showCost?: boolean;
};

/** Header/cell class the grid styles for the centred icon column. */
export const HISTORY_CENTER_CLASS = 'maia-history-center';
/** Numeric cells keep digits the same width so columns of times/counts line up. */
export const HISTORY_NUMERIC_CLASS = 'maia-history-numeric';

export const numberComparator = (a: number | null, b: number | null) =>
  (a ?? -1) - (b ?? -1);

export const isoComparator = (a: string | null, b: string | null) =>
  (a || '').localeCompare(b || '');

/**
 * Last column of both history grids: a document icon that opens the row
 * read only, full size. colId 'view' (a click there does not open the row).
 */
export const viewColumn = (
  title: string,
  onOpen: (data: unknown) => void,
): ColDef => ({
  colId: 'view',
  headerName: '',
  width: 40,
  minWidth: 40,
  maxWidth: 40,
  sortable: false,
  resizable: false,
  suppressMenu: true,
  suppressSizeToFit: true,
  headerClass: HISTORY_CENTER_CLASS,
  cellClass: HISTORY_CENTER_CLASS,
  cellStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  cellRendererFramework: ({ data }: ICellRendererParams) =>
    data ? (
      <ActionIcon
        Icon={DescriptionIcon}
        title={title}
        aria-label={title}
        onClick={() => onOpen(data)}
        style={{ margin: 0, width: 24, height: 24 }}
      />
    ) : null,
});

const rowOf = (data: unknown) => data as PatientConversationSummary | undefined;

const rowPerson = ({ data }: { data?: unknown }) => {
  const row = rowOf(data);
  return row ? personLabel(row) : '';
};

const conversationCells = (data: unknown): TokenCells | undefined => {
  const row = rowOf(data);
  return row
    ? {
        tokens: row.totalTokensUsed,
        input: row.totalInputTokens,
        cached: row.totalCachedTokens,
        output: row.totalOutputTokens,
        candidates: row.totalCandidatesTokens,
        thoughts: row.totalThoughtsTokens,
        questionsCost: row.questionsCostUsd,
        cacheCost: row.cacheCostUsd,
        totalCost: row.totalEstimatedCostUsd,
      }
    : undefined;
};

const rowTopic = ({ data }: { data?: unknown }) => {
  const row = rowOf(data);
  return row ? conversationTopic(row) : '';
};

/**
 * One row per conversation, opened in place to its questions:
 * Date (started) · Person · Topic · Model (latest answer) · Messages (answered
 * questions) · Duration (sum of the answers' times) · Tokens (with "See
 * tokens" on, the token split and cost too) · view (the whole conversation,
 * full size).
 * The tile drops Person, Model and Messages so the rest fits its ~425px with no sideways
 * scroll (130 + Topic >= 60 + 52 + 72 + 40); expanded, Person and Topic
 * share what the fixed columns leave.
 */
export const getHistoryColumnDefs = ({
  onOpenPast,
  smallWidget,
  siteTimezone,
  showCost,
}: ColumnDefsArgs): ColDef[] => [
  {
    colId: 'startedAt',
    field: 'startedAt',
    headerName: defaults.historyDate,
    width: smallWidget ? 130 : 150,
    sort: 'desc',
    comparator: isoComparator,
    // the chevron that opens the row's questions and answers
    cellRenderer: 'agGroupCellRenderer',
    valueFormatter: ({ data }: ValueFormatterParams) =>
      formatDateTime(rowOf(data)?.startedAt, siteTimezone),
  },
  {
    colId: 'person',
    field: 'userDisplayName',
    headerName: defaults.historyPerson,
    flex: 1,
    minWidth: 72,
    // the tile has no room; the full-size summary still names the person
    hide: smallWidget,
    valueGetter: rowPerson,
    tooltipValueGetter: rowPerson,
  },
  {
    colId: 'topic',
    field: 'topic',
    headerName: defaults.historyTopic,
    flex: 1,
    minWidth: 60,
    valueGetter: rowTopic,
    // the whole topic, which the cell cuts short
    tooltipValueGetter: rowTopic,
  },
  {
    colId: 'model',
    field: 'lastResponseModel',
    headerName: defaults.historyModel,
    width: 80,
    hide: smallWidget,
    valueFormatter: ({ value }: ValueFormatterParams) => modelFamily(value),
    tooltipField: 'lastResponseModel',
  },
  {
    colId: 'messages',
    field: 'questionCount',
    headerName: defaults.historyMessages,
    width: 85,
    hide: smallWidget,
    cellClass: HISTORY_NUMERIC_CLASS,
    comparator: numberComparator,
  },
  {
    colId: 'duration',
    field: 'durationSeconds',
    headerName: defaults.historyDuration,
    width: smallWidget ? 52 : 80,
    cellClass: HISTORY_NUMERIC_CLASS,
    comparator: numberComparator,
    valueFormatter: ({ value }: ValueFormatterParams) => formatDuration(value),
  },
  ...getTokenColumnDefs({ cells: conversationCells, showCost, smallWidget }),
  viewColumn(defaults.viewConversation, (data) =>
    onOpenPast(data as PatientConversationSummary),
  ),
];
